import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import axios from 'axios';
// FIX: Add 'format as formatFns' to the date-fns import
import { format, parse, isWithinInterval, isValid as isDateValid, addDays, format as formatFns } from 'date-fns';
import { FaPlus, FaTimes, FaEdit, FaCalendarAlt, FaChevronRight, FaChevronLeft, FaCheck } from 'react-icons/fa';

// Context & API
import { useAuth } from '../../context/AuthContext';
import { fetchProjectsByUserApi, saveOrUpdateRoleApi, fetchRoleByIdApi, fetchRoleListApi } from '../../services/api';
import { fetchLookupDataApi } from '../../services/lookupApi';
import MetaTags from '../../components/MetaTags';

// Components
import RoleWizard from '../../components/role/RoleWizard';
import StepRoleDetails from '../../components/role/StepRoleDetails';
import StepSceneScript from '../../components/role/StepSceneScript';
import StepAuditionLocations from '../../components/role/StepAuditionLocations';
import StepSchedule from '../../components/role/StepSchedule';
import StepRoleSeoMeta from '../../components/role/StepRoleSeoMeta';

// Utils & Schema
import { roleSchema } from '../../validation/roleSchema';
import { CACHE_KEY_LOOKUPS, LOOKUP_MAPPING, STATIC_OPTIONS, getWizardSteps } from '../../utils/roleConstants'; // Use getWizardSteps

import { 
    getNestedError, safeFormat, transformToSelectOptions, 
    stripHtmlFromFrontend, mapMultiSelect, countWorkingDays,
    calculateLiveAuditionSeats, calculateOnlineAuditionSeats, createDefaultSchedule,
    toMultiSelectOptions
} from '../../utils/roleHelpers';
import './AddEditRolePage.css';
import CryptoJS from "crypto-js";

const AddEditRolePage = ({ selectedRoleId, cloneRoleData, setActiveTab }) => {
    const navigate = useNavigate();
    const params = useParams();
    const roleId = selectedRoleId || params.roleId || params.id;
    const isEditMode = !!roleId;
    
    const { user } = useAuth(); // Get user from AuthContext
    const isAdminTokenPresent = !!localStorage.getItem('adminToken'); // Check local storage for admin login
    const isDirectorOrCastingDirector = user?.roleCode === '200' || user?.roleCode === '400';
    // --- FIX 2: Determine if current user has access to SEO Meta tab ---
    const canAccessSeoMetaTab = isAdminTokenPresent; // Only Admin can see/edit SEO
    // --- FIX 3: Get dynamic wizard steps based on admin access ---
    const dynamicWizardSteps = useMemo(() => getWizardSteps(canAccessSeoMetaTab), [canAccessSeoMetaTab]);

    const [projectStories, setProjectStories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pageLoading, setPageLoading] = useState(true);
    const [apiMessage, setApiMessage] = useState({ type: '', text: '' });
    const [lookupOptions, setLookupOptions] = useState(STATIC_OPTIONS);
    const [projectList, setProjectList] = useState([]);
    const [currentStep, setCurrentStep] = useState(0);

    // --- SCHEDULE STATE MANAGEMENT ---
    const [schedulesData, setSchedulesData] = useState({});
    const [isSchedulingEdit, setIsSchedulingEdit] = useState(false);
    const [showCalendar, setShowCalendar] = useState(false);
    const [selectedScheduleType, setSelectedScheduleType] = useState(null);
    const [calDate, setCalDate] = useState(new Date());
    const [dateSpecificOffSlots, setDateSpecificOffSlots] = useState({});
    const [allRoles, setAllRoles] = useState([]); // Used for project stories
    
    // --- PROJECT DATE CONSTRAINT STATES ---
    const [projectDateRanges, setProjectDateRanges] = useState(new Map()); 
    const [minProjectDate, setMinProjectDate] = useState(null);
    const [maxProjectDate, setMaxProjectDate] = useState(null);
    const hasAdminToken = !!localStorage.getItem('adminToken');
    const { control, handleSubmit, reset, watch, trigger, setValue, getValues, formState: { errors } } = useForm({
        resolver: yupResolver(roleSchema),
        mode: 'onChange',
        defaultValues: { 
            // --- FIX 1: Set default status as an object ---
            // status: STATIC_OPTIONS.status[0], // Pass the full object
            status: STATIC_OPTIONS?.status?.[0] || null,
            audition_type: [], 
            story_line: '',
            auditionLocations: [{ pincode: '', address1: '', address2: '', state: '', district: '' }],
            metaTitle: '',
            online_seats_per_day_input: 0 
        }
    });
    const SECRET = process.env.REACT_APP_CRYPTO_SECRET;
    const normalizeScheduleType = (type) =>
    String(type || '')
        .toLowerCase()
        .replace(/\s+/g, '')
        .replace(/-/g, '');

    const encryptLocal = (data) => {
    return CryptoJS.AES.encrypt(
        JSON.stringify(data),
        SECRET
    ).toString();
    };

    const decryptLocal = (data) => {
    const bytes = CryptoJS.AES.decrypt(data, SECRET);
    return JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
    };

    // --- Watchers for form fields ---
    const watchedAuditionType = watch('audition_type');
    // Note: 'casting_location' is at index 7 in this watched array based on the `watch` call in StepSchedule.
    // Ensure this index is consistent if you modify the watchedScheduleForm array structure in StepSchedule.
    const watchedScheduleForm = watch(['start_date', 'end_date', 'start_time', 'end_time', 'casting_time', 'week_days_off', 'role_required_count', 'casting_location', 'online_seats_per_day_input']);
    const [watchedStartDate, watchedEndDate, watchedStartTime, watchedEndTime, watchedCastingTime, watchedWeekDaysOff, , , watchedOnlineSeatsPerDay] = watchedScheduleForm;
    const watchedLocations = watch('auditionLocations');
    const watchedProjectSelection = watch('project_name');

    // --- Derived States ---
    const isCurrentVideo = useMemo(() => {
        if (!selectedScheduleType) return false;
        const label = selectedScheduleType.label.toLowerCase();
        return label.includes('video') || label.includes('upload') || label.includes('online');
    }, [selectedScheduleType]);

    const locationOptions = useMemo(() => {
        if (!watchedLocations || watchedLocations.length === 0) return [];
        return watchedLocations
            .filter(loc => loc.address1 && loc.district)
            .map(loc => ({ label: `${loc.address1}, ${loc.district}`, value: `${loc.address1}, ${loc.district}` }));
    }, [watchedLocations]);
    
    const liveAuditionCalculations = useMemo(() => 
        calculateLiveAuditionSeats(watchedScheduleForm, dateSpecificOffSlots, countWorkingDays, isCurrentVideo, setValue),
        [watchedScheduleForm, dateSpecificOffSlots, isCurrentVideo, setValue]
    );

    const onlineAuditionCalculations = useMemo(() => 
        calculateOnlineAuditionSeats(watchedScheduleForm, countWorkingDays, isCurrentVideo, setValue),
        [watchedScheduleForm, isCurrentVideo, setValue]
    );

    // --- Effects ---
    useEffect(() => {
        const fetchRoles = async () => {
            try {
                const res = await fetchRoleListApi();
                const roles = res?.data?.data || res?.data || [];
                setAllRoles(roles);
            } catch (err) {
                console.error("❌ ERROR fetching roles:", err);
            }
        };
        fetchRoles();
    }, []);

    useEffect(() => {
        if (watchedProjectSelection && projectDateRanges.has(watchedProjectSelection.value || watchedProjectSelection)) {
            const { projectStartDate, projectEndDate } = projectDateRanges.get(watchedProjectSelection.value || watchedProjectSelection);
            setMinProjectDate(projectStartDate);
            setMaxProjectDate(projectEndDate);
        } else {
            setMinProjectDate(null);
            setMaxProjectDate(null);
        }
    }, [watchedProjectSelection, projectDateRanges]);
    
    useEffect(() => {
        if (watchedProjectSelection && allRoles?.length) {
            const projectId = watchedProjectSelection?.value || watchedProjectSelection;
            const sameProjectRoles = allRoles.filter(r => String(r.project_joget_id || r.projectid) === String(projectId));

            const stories = sameProjectRoles
                .filter(r => r.story_line && r.story_line !== "<p><br></p>" && r.story_line.trim() !== "")
                .map((r) => {
                    const cleanText = r.story_line.replace(/<[^>]*>/g, '').trim();
                    return { label: cleanText.substring(0, 50) || "Story", value: r.story_line };
                });
            setProjectStories(stories);
        }
    }, [watchedProjectSelection, allRoles]);

    // --- Callbacks ---
    const handlePincodeLookup = async (pincode, index) => {
        if (!pincode || pincode.length !== 6) return;
        try {
            // const response = await axios.get(`https://api.postalpincode.in/pincode/${pincode}`);
            let response;
                try {
                    // 🔥 FIRST API
                    response = await axios.get(`https://api.postalpincode.in/pincode/${pincode}`);

                    // 🔥 IF FAILED THEN BACKUP API
                    if (!response.data?.[0] || response.data?.[0]?.Status !== "Success") {
                        response = await axios.get(`http://www.postalpincode.in/api/pincode/${pincode}`);
                        
                        // 🔥 BACKUP RESPONSE FORMAT FIX
                        response.data = [{
                            Status: response.data.Status,
                            PostOffice: response.data.PostOffice
                        }];
                    }
                } catch {
                    // 🔥 DIRECT BACKUP API
                    response = await axios.get(`http://www.postalpincode.in/api/pincode/${pincode}`);
                    
                    response.data = [{
                        Status: response.data.Status,
                        PostOffice: response.data.PostOffice
                    }];
                }
            // if (response.data?.[0]?.Status === 'Success') {
            if (response?.data?.[0]?.Status === 'Success') {
                const { State, District } = response.data[0].PostOffice[0];
                setValue(`auditionLocations.${index}.state`, State, { shouldValidate: true });
                setValue(`auditionLocations.${index}.district`, District, { shouldValidate: true });
            } else setApiMessage({ type: 'warning', text: `Pincode not found for location #${index + 1}` });
        } catch (error) { console.error("Pincode API Error"); }
    };

    const loadData = useCallback(async () => {
        setPageLoading(true);
        try {
            const projectReq = fetchProjectsByUserApi();
            let cachedLookups = localStorage.getItem(CACHE_KEY_LOOKUPS);
            let finalLookups = {};

            if (cachedLookups) {
                // finalLookups = JSON.parse(cachedLookups);
                finalLookups = decryptLocal(cachedLookups);
            } else {
                const keys = Object.keys(LOOKUP_MAPPING);
                const responses = await Promise.all(keys.map(key => fetchLookupDataApi(LOOKUP_MAPPING[key])));
                keys.forEach((key, i) => finalLookups[key] = transformToSelectOptions(responses[i].data.data, 'lookupValue', 'lookupLabel'));
                finalLookups = { ...finalLookups, ...STATIC_OPTIONS };
                // localStorage.setItem(CACHE_KEY_LOOKUPS, JSON.stringify(finalLookups));
                localStorage.setItem(CACHE_KEY_LOOKUPS, encryptLocal(finalLookups));
                console.log( "AUDITION TYPES =>", finalLookups.audition_type);
            }
            setLookupOptions(finalLookups);

            const projectRes = await projectReq;
            const fetchedProjects = projectRes.data.data || [];
            const projectOptions = fetchedProjects.map(p => {
                const startDate = p.start_date ? parse(p.start_date, 'yyyy-MM-dd', new Date()) : null;
                const endDate = p.end_date ? parse(p.end_date, 'yyyy-MM-dd', new Date()) : null;
                
                if (startDate && endDate) {
                    setProjectDateRanges(prev => {
                        const newMap = new Map(prev);
                        newMap.set(p.joget_project_id || p.id, { projectStartDate: startDate, projectEndDate: endDate });
                        return newMap;
                    });
                }
                return { value: p.joget_project_id || p.id, label: p.project_name || p.projectName };
            });
            setProjectList(projectOptions);

            if (isEditMode) {
                const response = await fetchRoleByIdApi(roleId);
                const raw = response.data?.data; 

                if (raw) {
                      console.log("FULL RAW DATA 👉", raw);  
                          console.log("🔥 SCHEDULES 👉", raw.schedules);
                              console.log("🔥 FIRST SCHEDULE 👉", raw.schedules?.ONLINE || raw.schedules?.[0]); 
                    // const auditionArray = toMultiSelectOptions(raw.audition_type || raw.auditionType, finalLookups.audition_type);
                    const auditionArray = toMultiSelectOptions( raw.audition_type || raw.auditionType,finalLookups?.audition_type || []);

                    let parsedLocations = [];
                    try {
                        parsedLocations = typeof raw.audition_locations_json === 'string' 
                            ? JSON.parse(raw.audition_locations_json) 
                            : (raw.auditionLocations || []);
                    } catch (e) { parsedLocations = []; }

                    // Reset with fetched data
                    reset({
                        ...raw,
                        id: raw.id,
                        project_name: projectOptions.find(p => p.value === (raw.projectid || raw.project_joget_id)) || null,
                        audition_type: auditionArray,
                        role_name: raw.roleName || raw.role_name,
                        // --- FIX 1: Find the status object for reset ---
                        // status: finalLookups.status.find(opt => opt.value === (raw.status || 'active')) || STATIC_OPTIONS.status[0], 
                        status: finalLookups?.status?.find(opt => opt.value === (raw.status || 'active')) || STATIC_OPTIONS?.status?.[0] || null,
                        // special_category: finalLookups.special_category.find(opt => opt.value === (raw.special_category || '')) || STATIC_OPTIONS.special_category[0], 
                        special_category: finalLookups?.special_category?.find(opt => opt.value === (raw.special_category || '') ) || STATIC_OPTIONS?.special_category?.[0] ||null,                        
                        role_category: toMultiSelectOptions(raw.roleCategory || raw.role_category, finalLookups.role_category),
                        talent: toMultiSelectOptions(raw.requiredTalent || raw.required_talent, finalLookups.talent),
                        gender: toMultiSelectOptions(raw.requiredGender || raw.required_gender, finalLookups.gender),
                        language: toMultiSelectOptions(raw.requiredLanguage || raw.required_language, finalLookups.language),
                        nationality: toMultiSelectOptions(raw.requiredNationality || raw.required_nationality, finalLookups.nationality),
                        dialect: toMultiSelectOptions(raw.requiredDialect || raw.required_dialect, finalLookups.dialect),
                        experience_level: toMultiSelectOptions(raw.requiredExperienceLevel || raw.required_experience_level, finalLookups.experience_level),
                        skin_color: toMultiSelectOptions(raw.requiredSkinColor || raw.required_skin_color, finalLookups.skin_color),
                        age_year: toMultiSelectOptions(raw.requiredAge || raw.required_age, finalLookups.age_year),
                        hair_color: toMultiSelectOptions(raw.requiredHairColor || raw.required_hair_color, finalLookups.hair_color),
                        body_type: toMultiSelectOptions(raw.requiredBodyType || raw.required_body_type, finalLookups.body_type),
                        hair_type: toMultiSelectOptions(raw.requiredHairType || raw.required_hair_type, finalLookups.hair_type),
                        height_ft: toMultiSelectOptions(raw.requiredHeight || raw.required_height, finalLookups.height_ft),
                        eye_color: toMultiSelectOptions(raw.requiredEyeColor || raw.required_eye_color, finalLookups.eye_color),
                        waist_size: toMultiSelectOptions(raw.requiredWaistSize || raw.required_waist_size, finalLookups.waist_size),
                        chest_size: toMultiSelectOptions(raw.requiredChestSize || raw.required_chest_size, finalLookups.chest_size),
                        // special_category: toMultiSelectOptions(raw.requiredSpecialCategory || raw.special_category, finalLookups.special_category),
                        
                        role_required_count: raw.roleCount || 1,
                        role_description: raw.roleDescription || raw.role_description,
                        story_line: raw.story_line || '',
                        character_sketch: raw.character_sketch || '',
                        script_details: raw.script_details || raw.role_script || '',
                        scene_details: raw.scene_details || raw.role_scene || '',
                        
                        metaTitle: raw.metaTitle || raw.meta_title || raw.roleName || raw.role_name || '',
                        metaKeywords: raw.metaKeywords || raw.meta_keywords || '',
                        metaDescription: raw.metaDescription || raw.meta_description || '',
                        
                        auditionLocations: parsedLocations.length > 0 ? parsedLocations : [],
                        online_seats_per_day_input: 0 
                    });

                    // Handle Schedule State
                    if (raw.schedules || raw.roleScheduleJson) {
                        const schedSource = raw.schedules || raw.roleScheduleJson || [];
                        const initialSchedules = schedSource.reduce((acc, sched) => {
                            // const foundAuditionType = finalLookups.audition_type.find(at => at.value === sched.auditionType);
                            const foundAuditionType = finalLookups.audition_type.find(
                            at =>
                                String(at.value).toLowerCase().replace('-', '').replace(/\s+/g, '') ===
                                String(sched.auditionType).toLowerCase().replace('-', '').replace(/\s+/g, '')
                        );
                            // const isVideo = foundAuditionType?.label?.toLowerCase().includes('online'); // Not used here, but good to keep in mind

                            // acc[sched.auditionType] = {
                            const normalizedType = String(sched.auditionType)
                                .toLowerCase()
                                // .replace('-', '')
                                .replace(/-/g, '')
                                .replace(/\s+/g, '');

                            acc[normalizedType] = {
                                startDate: sched.startDate ? parse(sched.startDate, 'yyyy-MM-dd', new Date()) : null,
                                endDate: sched.endDate ? parse(sched.endDate, 'yyyy-MM-dd', new Date()) : null,
                                startTime: sched.startTime ? parse(sched.startTime.slice(0,5), 'HH:mm', new Date()): null, // Assuming HH:mm format for saved time
                                endTime: sched.endTime ? parse(sched.endTime.slice(0,5), 'HH:mm', new Date()): null,   // Assuming HH:mm format for saved time
                                castingTime: STATIC_OPTIONS.intervals.find(i => i.value === String(sched.castingTime)),
                                weekDaysOff: sched.weekDaysOff ? sched.weekDaysOff.split(';').map(label => STATIC_OPTIONS.week_days.find(d => d.label === label)).filter(Boolean) : [],
                                numberOfSeats: sched.numberOfSeats,
                                numberOfSeatsPerDay: sched.numberOfSeatsPerDay,
                                castingLocation: { label: sched.castingLocation, value: sched.castingLocation },
                                offSlotsData: sched.offSlotsData && sched.offSlotsData.length > 0 ? JSON.parse(sched?.offSlotsData || '{}') : {} // Parse offSlotsData from string
                            };
                            return acc;
                        }, {});
                        setSchedulesData(initialSchedules);
                    }
                }
            }
        } catch (e) {
            console.error("Error loading page data:", e);
            setApiMessage({ type: 'danger', text: 'Error loading role data.' });
        } finally {
            setPageLoading(false);
        }
    }, [roleId, reset, isEditMode, allRoles]); // Added allRoles to dependency array for project stories

    useEffect(() => { loadData(); }, [loadData]);
    
    const handleNext = async () => {
        // --- FIX 4: Use dynamicWizardSteps ---
        const fieldsToValidate = dynamicWizardSteps[currentStep].fields;
        const isValid = await trigger(fieldsToValidate);

        if (isValid) {
            if (currentStep < dynamicWizardSteps.length - 1) { setCurrentStep(c => c + 1); window.scrollTo(0, 0); }
            else handleSubmit(onSubmit)();
        } else setApiMessage({ type: 'danger', text: 'Please complete required fields.' });
    };

    const handleBack = () => { if (currentStep > 0) { setCurrentStep(curr => curr - 1); setIsSchedulingEdit(false); setShowCalendar(false); window.scrollTo(0, 0); } };
    
    const onSubmit = async (data) => {
        setLoading(true);
        try {
            const schedulesPayload = watchedAuditionType.map(opt => {
                console.log("🔥 schedulesData", schedulesData);
                // const scheduleInfo = schedulesData[opt.value];
                const scheduleInfo =
    schedulesData[normalizeScheduleType(opt.value)];
                // const isVideo = opt.label.toLowerCase().includes('online');
                const auditionType = String(opt.value || '')
                    .toLowerCase()
                    .replace(/\s+/g, '')
                    .replace('-', '');

                const isVideo =
                    auditionType.includes('online') ||
                    auditionType.includes('video');
                    const isMeeting = auditionType.includes('meeting');

                if (!scheduleInfo) {
                    // This block will create a default schedule if no scheduleInfo exists for an audition type.
                    // This is important if an audition type is selected but no schedule details were ever updated/saved for it.
                    return createDefaultSchedule(opt, projectDateRanges, data.project_name.value, countWorkingDays);
                }
                
                return {
                    auditionType: opt.value,
                    startDate: safeFormat(scheduleInfo.startDate, 'yyyy-MM-dd'),
                    endDate: safeFormat(scheduleInfo.endDate, 'yyyy-MM-dd'),
                    // Ensure time formats are consistent for backend, might need 'HH:mm:ss' or 'HH:mm'
                    startTime: scheduleInfo.startTime ? safeFormat(scheduleInfo.startTime, 'HH:mm') : (isVideo ? '' : '10:00'), 
                    endTime: scheduleInfo.endTime ? safeFormat(scheduleInfo.endTime, 'HH:mm') : (isVideo ? '' : '19:00'), 
                    castingTime: scheduleInfo.castingTime?.value || '15',
                    weekDaysOff: mapMultiSelect(scheduleInfo.weekDaysOff),
                    numberOfSeats: scheduleInfo.numberOfSeats,
                    numberOfSeatsPerDay: scheduleInfo.numberOfSeatsPerDay, 
                    up_offSlotsData: JSON.stringify(scheduleInfo.offSlotsData || {}),
                    // castingLocation: scheduleInfo.castingLocation?.value || (isVideo ? 'Web Upload' : 'TBD'),
                    castingLocation: isVideo
                    ? 'Web Upload'
                    : (
                        scheduleInfo?.castingLocation?.value ||
                        scheduleInfo?.castingLocation?.label ||
                        scheduleInfo?.castingLocation ||
                        ''
                    ),
                };
            });

            const cleanedStoryLine = stripHtmlFromFrontend(data.story_line || '');
            const cleanedScriptDetails = stripHtmlFromFrontend(data.script_details || '');
            const cleanedCharacterDetails = stripHtmlFromFrontend(data.character_sketch || ''); // Assuming this helper exists, or replace
            console.log(
            "🔥 SCHEDULE PAYLOAD FULL",
            JSON.stringify(schedulesPayload, null, 2)
            );
            const payload = { 
                ...data, 
                id: isEditMode ? roleId : '',
                status: data.status?.value || data.status,
                // status: data.status?.value || data.status,
                 special_category: data.special_category?.value || data.special_category,

                project_name: data.project_name?.value,
                
                audition_type: mapMultiSelect(data.audition_type),
                role_category: mapMultiSelect(data.role_category),
                gender: mapMultiSelect(data.gender),
                talent: mapMultiSelect(data.talent),
                language: mapMultiSelect(data.language),
                nationality: mapMultiSelect(data.nationality),
                age_year: mapMultiSelect(data.age_year),
                hair_color: mapMultiSelect(data.hair_color),
                body_type: mapMultiSelect(data.body_type),
                height_ft: mapMultiSelect(data.height_ft),
                eye_color: mapMultiSelect(data.eye_color),
                chest_size: mapMultiSelect(data.chest_size),
                waist_size: mapMultiSelect(data.waist_size),
                // special_category: mapMultiSelect(data.special_category),
                dialect: mapMultiSelect(data.dialect),
                skin_color: mapMultiSelect(data.skin_color),
                experience_level: mapMultiSelect(data.experience_level),
                
                required_talent: mapMultiSelect(data.talent), // For consistency with backend
                required_gender: mapMultiSelect(data.gender),
                required_height: mapMultiSelect(data.height_ft),
                required_body_type: mapMultiSelect(data.body_type),
                required_nationality: mapMultiSelect(data.nationality),
                required_experience_level: mapMultiSelect(data.experience_level),
                required_language: mapMultiSelect(data.language),
                required_dialect: mapMultiSelect(data.dialect),
                required_hair_color: mapMultiSelect(data.hair_color),
                required_hair_type: mapMultiSelect(data.hair_type),
                required_skin_color: mapMultiSelect(data.skin_color),
                required_eye_color: mapMultiSelect(data.eye_color),
                required_waist_size: mapMultiSelect(data.waist_size),
                required_chest_size: mapMultiSelect(data.chest_size),
                required_age: mapMultiSelect(data.age_year),

                story_line: cleanedStoryLine,
                script_details: cleanedScriptDetails,
                character_sketch: cleanedCharacterDetails,
                role_scene: data.scene_details,
                role_script: data.script_details, // Assuming script_details is the source
                
                schedules: schedulesPayload, 
                audition_locations_json: JSON.stringify(data.auditionLocations),
                metaTitle: data.metaTitle || data.roleName || '',
                metaKeywords: data.metaKeywords || '',
                metaDescription: data.metaDescription || '',
            };
            // --- FIX 1: Send only the value of the status object ---
            payload.status = data.status?.value; 

            await saveOrUpdateRoleApi(payload);
            setApiMessage({ type: 'success', text: `Role Saved Successfully!` });
            
            setTimeout(() => {
                if (setActiveTab){
                    setActiveTab("roles");
                } 
                else {
                    if(isAdminTokenPresent){
                        navigate("/admin/roles");
                    }else{
                        navigate("/dashboard/roles");
                    }
                }
            }, 1000);

        } catch (e) { 
            console.error("Submission Error:", e);
            setApiMessage({ type: 'danger', text: 'Operation Failed.' }); 
        } finally { setLoading(false); }
    };
    
    const handleUpdateScheduleClick = (scheduleType) => {
        setSelectedScheduleType(scheduleType);
        // const existingData = schedulesData[scheduleType.value];
        const existingData =
    schedulesData[normalizeScheduleType(scheduleType.value)];
        const isVideo = scheduleType.label.toLowerCase().includes('online') && !scheduleType.label.toLowerCase().includes('meeting');

        const projStart = minProjectDate || new Date();
        const projEnd = maxProjectDate || addDays(new Date(), 90);

        // Date Defaults within boundaries
        setValue('start_date', existingData?.startDate || projStart);
        setValue('end_date', existingData?.endDate || projEnd);
        setCalDate(existingData?.startDate || projStart);

        setValue('casting_time', existingData?.castingTime || STATIC_OPTIONS.intervals[0]);
        // Filter out any null/undefined entries from weekDaysOff
        setValue('week_days_off', existingData?.weekDaysOff?.filter(Boolean) || [STATIC_OPTIONS.week_days[0]]);
        const isFaceToFace = scheduleType.label.toLowerCase().includes('face');
        if (isVideo) {
                setValue('online_seats_per_day_input', existingData?.numberOfSeatsPerDay || 10);
                setValue('role_required_count', existingData?.numberOfSeats || 0); 
                setValue('casting_location', { label: 'Web Upload', value: 'Web Upload' });
                setValue('start_time', null); 
                setValue('end_time', null); 
            } else if (isFaceToFace) {
                setValue('online_seats_per_day_input', 0);
                setValue('role_required_count', existingData?.numberOfSeats || 0);
                setValue('casting_location', {
                    label: 'Meeting Link',
                    value: 'Meeting Link'
                });

        } else {
                setValue('online_seats_per_day_input', 0); 
                setValue('role_required_count', existingData?.numberOfSeats || 0); 
                setValue('casting_location', existingData?.castingLocation || null);
                // Ensure startTime and endTime are parsed correctly from saved string to Date object for react-datepicker
                // FIX: Use formatFns imported at the top for formatting.
                setValue('start_time', existingData?.startTime ? parse(formatFns(existingData.startTime, 'hh:mm a'), 'hh:mm a', new Date()) : null);
                setValue('end_time', existingData?.endTime ? parse(formatFns(existingData.endTime, 'hh:mm a'), 'hh:mm a', new Date()) : null);
        }

        setDateSpecificOffSlots(existingData?.offSlotsData || {});
        setIsSchedulingEdit(true);
    };
    
    const handleDoneEditing = async () => {
        if (!selectedScheduleType) return;
        
        // Validate schedule specific fields before saving it to schedulesData
        const scheduleFieldsToValidate = [
            'start_date', 'end_date', 'week_days_off', 'casting_location', 'casting_time', 'online_seats_per_day_input', 'start_time', 'end_time'
        ].filter(field => {
            if (isCurrentVideo) return !['casting_time', 'start_time', 'end_time'].includes(field);
            else return !['online_seats_per_day_input'].includes(field);
        });

        const isValid = await trigger(scheduleFieldsToValidate);
        
        if (!isValid) {
            setApiMessage({ type: 'danger', text: 'Please fill all required schedule fields.' });
            window.scrollTo(0,0);
            return;
        }

        const currentFormData = getValues();
        const isVideo = selectedScheduleType.label.toLowerCase().includes('online') &&  !selectedScheduleType.label.toLowerCase().includes('meeting');

        let numberOfSeats, numberOfSeatsPerDay;
        if (isVideo) {
            numberOfSeatsPerDay = parseInt(currentFormData.online_seats_per_day_input, 10) || 0;
            numberOfSeats = onlineAuditionCalculations.total; 
        } else {
            numberOfSeats = liveAuditionCalculations.total;
            numberOfSeatsPerDay = liveAuditionCalculations.perDay;
        }

        const updatedSchedule = {
            startDate: currentFormData.start_date,
            endDate: currentFormData.end_date, // FIX: Corrected to currentFormData.end_date
            startTime: currentFormData.start_time,
            endTime: currentFormData.end_time,
            castingTime: currentFormData.casting_time,
            weekDaysOff: currentFormData.week_days_off,
            numberOfSeats: numberOfSeats,
            numberOfSeatsPerDay: numberOfSeatsPerDay,
            castingLocation: currentFormData.casting_location,
            offSlotsData: dateSpecificOffSlots,
            online_seats_per_day_input: isVideo ? currentFormData.online_seats_per_day_input : 0, 
        };

        // setSchedulesData(prev => ({ ...prev, [selectedScheduleType.value]: updatedSchedule }));
        
        setSchedulesData(prev => ({
    ...prev,
    [normalizeScheduleType(selectedScheduleType.value)]: updatedSchedule
}));
        setIsSchedulingEdit(false);
        setSelectedScheduleType(null);
        setDateSpecificOffSlots({});
        setApiMessage({ type: 'success', text: `Schedule for ${selectedScheduleType.label} updated.` });
    };

    const toggleSlotAvailability = (time) => {
        const dateKey = format(calDate, 'yyyy-MM-dd');
        const current = dateSpecificOffSlots[dateKey] || [];
        const updated = current.includes(time) ? current.filter(t => t !== time) : [...current, time];
        setDateSpecificOffSlots({ ...dateSpecificOffSlots, [dateKey]: updated });
    };

    if (pageLoading) return <div className="text-center p-5"><Spinner animation="border" variant="danger" /></div>;

    return (
        <div className="add-edit-role-page">
            <MetaTags title={isEditMode ? 'Edit Role' : 'Add Role'} />
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="text-uppercase fw-bold text-white mb-0"><span className="text-danger border-start border-4 border-danger ps-3">{isEditMode ? 'Edit' : 'Create'} Role</span></h4>
            </div>

            {/* If not in editing mode, the wizard shows. If in editing mode, the schedule editing form takes precedence. */}
            {!isSchedulingEdit && <RoleWizard currentStep={currentStep} steps={dynamicWizardSteps} />}
            
            {apiMessage.text && <Alert variant={apiMessage.type} dismissible onClose={() => setApiMessage({ type: '', text: '' })}>{apiMessage.text}</Alert>}

            <div className="custom-card-box">
                <Form noValidate onSubmit={handleSubmit(onSubmit)}>
                    {currentStep === 0 && <StepRoleDetails control={control} errors={errors} lookupOptions={lookupOptions} projectList={projectList} />}
                    {currentStep === 1 && <StepSceneScript control={control} projectStories={projectStories} setValue={setValue} />}
                    {currentStep === 2 && <StepAuditionLocations control={control} errors={errors} handlePincodeLookup={handlePincodeLookup} />}
                    {currentStep === 3 && (
                        <StepSchedule
                            control={control} errors={errors} lookupOptions={lookupOptions} locationOptions={locationOptions}
                            watchedAuditionType={watchedAuditionType} schedulesData={schedulesData}
                            isSchedulingEdit={isSchedulingEdit} handleUpdateScheduleClick={handleUpdateScheduleClick} handleDoneEditing={handleDoneEditing}
                            selectedScheduleType={selectedScheduleType} isCurrentVideo={isCurrentVideo} watchedScheduleForm={watchedScheduleForm}
                            liveAuditionCalculations={liveAuditionCalculations} onlineAuditionCalculations={onlineAuditionCalculations}
                            minProjectDate={minProjectDate} maxProjectDate={maxProjectDate}
                            showCalendar={showCalendar} setShowCalendar={setShowCalendar} calDate={calDate} setCalDate={setCalDate}
                            dateSpecificOffSlots={dateSpecificOffSlots} toggleSlotAvailability={toggleSlotAvailability}
                        />
                    )}
                    {dynamicWizardSteps[currentStep]?.component === 'seo' && (
                        <StepRoleSeoMeta control={control} errors={errors} />
                    )}
                    {/* {currentStep === 4 && ( <StepRoleSeoMeta control={control} errors={errors} />)} */}
                </Form>
            </div>

            <div className="d-flex justify-content-between mt-4">
                <Button variant="outline-secondary" onClick={handleBack} disabled={currentStep === 0 || loading} size="lg" className="px-4 border-secondary text-light"><FaChevronLeft className="me-2" /> Back</Button>
                <Button variant="danger" onClick={handleNext} disabled={loading} size="lg" className="px-5 fw-bold shadow-lg" style={{ minWidth: '180px' }}>{loading ? <Spinner animation="border" size="sm" className='text-white'/> : (currentStep === dynamicWizardSteps.length - 1 ? 'SUBMIT ROLE' : <>Next <FaChevronRight className="ms-2" /></>)}</Button> {/* Use dynamicSteps.length */}
            </div>
        </div>
    );
};

export default AddEditRolePage;